import { UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { FileType, PurchaseRequestFilePayload } from "../../../types";
import { fileText } from "../../../utils/utils";
import axios from "../../../axios-orders";
import { generateClientId } from "./types";
import type { RequestFormValues } from "./requestFormSchema";

type GetValues = UseFormGetValues<RequestFormValues>;
type SetValue = UseFormSetValue<RequestFormValues>;

/**
 * The S3 upload + request-level attachment handlers, factored out of `useNewPurchaseRequest`. The
 * upload stays the exact imperative POST the pre-Carbon page used (`/utils/upload-s3`), bridged into
 * the RHF `files` field. `uploadFile` is also handed down to each item's `ItemFilePicker`.
 */
export const createFileHandlers = (
  getValues: GetValues,
  setValue: SetValue,
  setIsUploadingAttachment: (uploading: boolean) => void
) => {
  const uploadFile = async (
    file: File
  ): Promise<PurchaseRequestFilePayload> => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await axios.post("/utils/upload-s3", formData);
    const fileURL: string =
      typeof data === "string" ? data : (data?.fileURL ?? data?.url ?? "");
    return {
      clientId: generateClientId(),
      fileURL,
      name: file.name || fileText(FileType.ATTACHMENT),
      type: FileType.ATTACHMENT,
    };
  };

  const handleAttachmentUpload = async (file: File) => {
    setIsUploadingAttachment(true);
    try {
      const uploaded = await uploadFile(file);
      setValue("files", [...getValues("files"), uploaded]);
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleRemoveFile = (fileKey: string) => {
    setValue(
      "files",
      getValues("files").filter(
        (file) =>
          file.clientId !== fileKey &&
          file.id !== fileKey &&
          file.fileURL !== fileKey
      )
    );
  };

  return { uploadFile, handleAttachmentUpload, handleRemoveFile };
};
